/**
 * MidManagement — Gestion des MID Supervisors
 * Onboarding, invitations, liste et promotion directe
 */

import { PUBLIC_APP_URL } from '../../config';
import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { Title, useNotify } from 'react-admin';

import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';

import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import GroupsIcon from '@mui/icons-material/Groups';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import LinkIcon from '@mui/icons-material/Link';

import { GET_ADMIN_USERS } from '../users/user.queries';

// ─── Config ─────────────────────────────────────────────────────────────────

const API_URL = import.meta.env.VITE_API_URL || 'https://api.e-kaa.app';
const TOKEN_KEY = 'ekaa_admin_token';

function getAdminToken(): string {
  return localStorage.getItem(TOKEN_KEY) || '';
}

async function apiPost(path: string, body: object) {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAdminToken()}`,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || `Erreur ${res.status}`);
  return data;
}

async function apiGet(path: string) {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${getAdminToken()}` },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || `Erreur ${res.status}`);
  return data;
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface MidUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  city: string | null;
  country: string | null;
  createdAt: string;
  accountStatus: string;
  role: string;
}

interface Invitation {
  id: string;
  code: string;
  role: string;
  maxUses: number;
  useCount: number;
  isActive: boolean;
  expiresAt: string | null;
  createdAt: string;
  usedBy?: { id: string; email: string | null } | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function StatsCard({ title, value, icon, color, loading }: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2,
          bgcolor: `${color}20`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', color,
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {loading ? <CircularProgress size={20} /> : value}
          </Typography>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Modal : Gestion d'équipe d'un MID ──────────────────────────────────────

interface TeamMember {
  id: string;
  userId: string;
  tier: string;
  referralCode: string;
  university: string;
  isActive: boolean;
  activatedAt: string;
  user: {
    id: string;
    email: string | null;
    phone: string | null;
    profile: {
      displayName: string | null;
      avatarUrl: string | null;
      city: string | null;
      country: string | null;
    } | null;
  };
  profileCompleteness: number;
  stats: {
    totalCards: number;
    publishedCards: number;
    weeklyCards: number;
    totalViews: number;
    weeklyObjective: number | null;
    pendingCommissions: number;
    totalEarned: number;
  };
}

interface TeamStats {
  totalAmbassadors: number;
  activeAmbassadors: number;
  inactiveAmbassadors: number;
  totalCards: number;
  weeklyCards: number;
  totalViews: number;
  pendingCommissions: number;
  totalCommissionsPaid: number;
}

function TeamManagementDialog({ mid, open, onClose }: { mid: MidUser | null; open: boolean; onClose: () => void }) {
  const notify = useNotify();
  const [tab, setTab] = useState(0);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [stats, setStats] = useState<TeamStats | null>(null);
  const [loading, setLoading] = useState(true);

  const loadTeamData = useCallback(async () => {
    if (!mid) return;
    setLoading(true);
    try {
      console.log('🔍 Loading team data for MID:', mid.id, mid.displayName);
      const [teamData, statsData] = await Promise.all([
        apiGet(`/api/mid-dashboard/team?midUserId=${mid.id}`),
        apiGet(`/api/mid-dashboard/stats?midUserId=${mid.id}`),
      ]);
      console.log('📊 API Response - Team:', teamData);
      console.log('📊 API Response - Stats:', statsData);
      console.log('📊 Team length:', Array.isArray(teamData) ? teamData.length : 'not an array');
      setTeam(teamData);
      setStats(statsData);
    } catch (e: any) {
      console.error('❌ Error loading team data:', e);
      notify(`Erreur: ${e.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [mid, notify]);

  useEffect(() => {
    if (open && mid) {
      loadTeamData();
    }
  }, [open, mid, loadTeamData]);

  const handleToggleActive = async (ambassadorId: string) => {
    try {
      await apiPost(`/api/mid-dashboard/ambassadeur/${ambassadorId}/toggle`, {});
      notify('Statut mis à jour', { type: 'success' });
      loadTeamData();
    } catch (e: any) {
      notify(`Erreur: ${e.message}`, { type: 'error' });
    }
  };

  if (!mid) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar src={mid.avatarUrl || undefined} sx={{ width: 48, height: 48 }}>
            {(mid.displayName || mid.firstName || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {mid.displayName || `${mid.firstName || ''} ${mid.lastName || ''}`.trim()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {mid.email} • {mid.city || 'Ville non spécifiée'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ ml: 'auto' }}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Stats globales */}
            {stats && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" fontWeight={700} color="primary.main">
                        {stats.totalAmbassadors}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ambassadeurs
                      </Typography>
                      <Typography variant="caption" color="success.main">
                        {stats.activeAmbassadors} actifs
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" fontWeight={700} color="secondary.main">
                        {stats.totalCards}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cartes créées
                      </Typography>
                      <Typography variant="caption" color="info.main">
                        {stats.weeklyCards} cette semaine
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" fontWeight={700} color="info.main">
                        {stats.totalViews.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Vues totales
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" fontWeight={700} color="success.main">
                        {stats.totalCommissionsPaid.toLocaleString()} XOF
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Commissions payées
                      </Typography>
                      <Typography variant="caption" color="warning.main">
                        {stats.pendingCommissions} en attente
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Tabs */}
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab icon={<GroupsIcon fontSize="small" />} iconPosition="start" label={`Équipe (${team.length})`} />
            </Tabs>

            {/* Liste des ambassadeurs */}
            {tab === 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>Ambassadeur</TableCell>
                      <TableCell>Université</TableCell>
                      <TableCell align="center">Profil</TableCell>
                      <TableCell align="center">Cartes</TableCell>
                      <TableCell align="center">Vues</TableCell>
                      <TableCell align="center">Objectif</TableCell>
                      <TableCell align="center">Statut</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {team.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            Aucun ambassadeur dans cette équipe
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      team.map((member) => (
                        <TableRow key={member.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar src={member.user.profile?.avatarUrl || undefined} sx={{ width: 32, height: 32 }}>
                                {(member.user.profile?.displayName || '?').charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {member.user.profile?.displayName || member.user.email}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {member.user.profile?.city || '—'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{member.university || '—'}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${member.profileCompleteness}%`}
                              size="small"
                              color={member.profileCompleteness >= 80 ? 'success' : member.profileCompleteness >= 50 ? 'warning' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={600}>
                              {member.stats.totalCards}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {member.stats.publishedCards} publiées
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">{member.stats.totalViews}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {member.stats.weeklyObjective || '—'}
                            </Typography>
                            {member.stats.weeklyCards > 0 && (
                              <Typography variant="caption" color="success.main">
                                {member.stats.weeklyCards} cette semaine
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={member.isActive ? 'Actif' : 'Inactif'}
                              size="small"
                              color={member.isActive ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={member.isActive ? 'Désactiver' : 'Activer'}>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleActive(member.id)}
                                color={member.isActive ? 'warning' : 'success'}
                              >
                                {member.isActive ? <PendingIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={loadTeamData} startIcon={<RefreshIcon />}>
          Rafraîchir
        </Button>
        <Button onClick={onClose} variant="contained">
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Tab 1 : Liste des MIDs ──────────────────────────────────────────────────

function MidListTab({ onPromote }: { onPromote: (user: MidUser) => void }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');

  const { data, loading, error, refetch } = useQuery(GET_ADMIN_USERS, {
    variables: {
      filter: { role: 'MID_SUPERVISOR', ...(search ? { search } : {}) },
      page: page + 1,
      pageSize: rowsPerPage,
    },
    fetchPolicy: 'cache-and-network',
  });

  const mids: MidUser[] = data?.adminUsers?.items || [];
  const total = data?.adminUsers?.total || 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Rechercher un MID..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
          }}
          sx={{ width: 300 }}
        />
        <Tooltip title="Rafraîchir">
          <IconButton onClick={() => refetch()}><RefreshIcon /></IconButton>
        </Tooltip>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {total} MID{total > 1 ? 's' : ''} au total
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell>MID Supervisor</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Ville</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Inscription</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : mids.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {search ? 'Aucun résultat' : 'Aucun MID Supervisor pour l\'instant'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              mids.map((mid) => (
                <TableRow key={mid.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={mid.avatarUrl || undefined} sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                        {(mid.displayName || mid.firstName || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {mid.displayName || `${mid.firstName || ''} ${mid.lastName || ''}`.trim() || '—'}
                        </Typography>
                        <Chip
                          label="MID SUPERVISOR"
                          size="small"
                          sx={{ bgcolor: '#7C3AED20', color: '#7C3AED', fontWeight: 600, fontSize: '0.65rem', height: 18 }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{mid.email || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{mid.city || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={mid.accountStatus === 'ACTIVE' ? 'Actif' : mid.accountStatus}
                      size="small"
                      color={mid.accountStatus === 'ACTIVE' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(mid.createdAt)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Voir l'équipe et gérer les ambassadeurs">
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => onPromote(mid)}
                        sx={{ fontSize: '0.7rem' }}
                      >
                        Gérer
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Par page"
        />
      </TableContainer>
    </Box>
  );
}

// ─── Tab 2 : Invitations ─────────────────────────────────────────────────────

function InvitationsTab({ refresh }: { refresh: number }) {
  const notify = useNotify();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/terrain-invitations');
      setInvitations(Array.isArray(data) ? data.filter((i: Invitation) => i.role === 'MID_SUPERVISOR') : []);
    } catch (e: any) {
      notify(`Erreur: ${e.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load, refresh]);

  const handleCopy = async (code: string) => {
    const link = `${PUBLIC_APP_URL}/rejoindre/invitation/${code}`;
    await navigator.clipboard.writeText(link).catch(() => {});
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    notify('Lien copié !', { type: 'success' });
  };

  const handleWhatsApp = (code: string) => {
    const link = `${PUBLIC_APP_URL}/rejoindre/invitation/${code}`;
    const msg = encodeURIComponent(
      `Bonjour ! Vous êtes invitée à rejoindre EKAA en tant que MID Supervisor.\n\nAccédez à votre invitation via ce lien :\n${link}\n\nBienvenue dans l'équipe EKAA ! ✨`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  if (loading) {
    return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  if (invitations.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <CardGiftcardIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography color="text.secondary" variant="body1">
          Aucune invitation MID créée.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Utilisez l'onglet <strong>Onboarder</strong> pour créer une invitation.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell>Code</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell>Utilisations</TableCell>
            <TableCell>Utilisé par</TableCell>
            <TableCell>Expiration</TableCell>
            <TableCell>Créé le</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invitations.map((inv) => {
            const isUsed = inv.useCount >= inv.maxUses;
            const isExpired = inv.expiresAt ? new Date(inv.expiresAt) < new Date() : false;
            const status = !inv.isActive || isExpired ? 'Expiré' : isUsed ? 'Utilisé' : 'Disponible';
            const statusColor = status === 'Disponible' ? 'success' : status === 'Utilisé' ? 'info' : 'default';

            return (
              <TableRow key={inv.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontFamily="monospace" fontWeight={600} color="primary.main">
                      {inv.code}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={status} size="small" color={statusColor as any} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{inv.useCount} / {inv.maxUses}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {inv.usedBy?.email || (isUsed ? 'Compte lié' : '—')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {inv.expiresAt ? formatDate(inv.expiresAt) : 'Pas de limite'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatDate(inv.createdAt)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="Copier le lien d'invitation">
                      <IconButton size="small" onClick={() => handleCopy(inv.code)} disabled={status !== 'Disponible'}>
                        {copiedCode === inv.code
                          ? <CheckCircleIcon fontSize="small" color="success" />
                          : <ContentCopyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Envoyer par WhatsApp">
                      <IconButton size="small" color="success" onClick={() => handleWhatsApp(inv.code)} disabled={status !== 'Disponible'}>
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Tab 3 : Onboarder un MID ────────────────────────────────────────────────

function OnboardTab({ onSuccess }: { onSuccess: () => void }) {
  const notify = useNotify();

  // Create invitation form
  const [maxUses, setMaxUses] = useState(1);
  const [expiresInDays, setExpiresInDays] = useState<number | ''>('');
  const [creatingInv, setCreatingInv] = useState(false);
  const [createdInvitation, setCreatedInvitation] = useState<{ code: string; link: string } | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  // Promote form
  const [promoteSearch, setPromoteSearch] = useState('');
  const [promoteResults, setPromoteResults] = useState<MidUser[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<MidUser | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [promoting, setPromoting] = useState(false);

  const { refetch: searchUsers } = useQuery(GET_ADMIN_USERS, {
    variables: { filter: { search: promoteSearch }, page: 1, pageSize: 5 },
    skip: true,
  });

  const handleCreateInvitation = async () => {
    setCreatingInv(true);
    try {
      const body: Record<string, unknown> = {
        role: 'MID_SUPERVISOR',
        maxUses: maxUses || 1,
      };
      if (expiresInDays) {
        const expDate = new Date();
        expDate.setDate(expDate.getDate() + Number(expiresInDays));
        body.expiresAt = expDate.toISOString();
      }
      const data = await apiPost('/api/terrain-invitations', body);
      const code = data.code;
      const link = `${PUBLIC_APP_URL}/rejoindre/invitation/${code}`;
      setCreatedInvitation({ code, link });
      notify('Invitation créée avec succès !', { type: 'success' });
      onSuccess();
    } catch (e: any) {
      notify(`Erreur: ${e.message}`, { type: 'error' });
    } finally {
      setCreatingInv(false);
    }
  };

  const handleSearch = async () => {
    if (!promoteSearch.trim()) return;
    setSearchLoading(true);
    try {
      const result = await searchUsers({
        filter: { search: promoteSearch.trim() },
        page: 1,
        pageSize: 5,
      });
      const items = result.data?.adminUsers?.items || [];
      setPromoteResults(items.filter((u: MidUser) => u.role !== 'MID_SUPERVISOR' && u.role !== 'ADMIN'));
    } catch (e: any) {
      notify(`Erreur: ${e.message}`, { type: 'error' });
    } finally {
      setSearchLoading(false);
    }
  };

  const handlePromote = async () => {
    if (!selectedUser) return;
    setPromoting(true);
    try {
      await apiPost('/api/terrain-invitations/promote', {
        targetUserId: selectedUser.id,
        newRole: 'MID_SUPERVISOR',
      });
      notify(`${selectedUser.displayName || selectedUser.email} est maintenant MID Supervisor !`, { type: 'success' });
      setConfirmOpen(false);
      setSelectedUser(null);
      setPromoteResults([]);
      setPromoteSearch('');
      onSuccess();
    } catch (e: any) {
      notify(`Erreur: ${e.message}`, { type: 'error' });
    } finally {
      setPromoting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!createdInvitation) return;
    await navigator.clipboard.writeText(createdInvitation.link).catch(() => {});
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleWhatsApp = () => {
    if (!createdInvitation) return;
    const msg = encodeURIComponent(
      `Bonjour ! Vous êtes invitée à rejoindre EKAA en tant que MID Supervisor.\n\nAccédez à votre invitation via ce lien :\n${createdInvitation.link}\n\nBienvenue dans l'équipe EKAA ! ✨`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  return (
    <Grid container spacing={3}>

      {/* ── Créer une invitation ── */}
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <CardGiftcardIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Créer une invitation MID</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Génère un code d'invitation unique à envoyer à la future MID. Elle devra créer un compte EKAA et utiliser le lien pour activer son rôle.
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Nombre d'utilisations"
              type="number"
              size="small"
              value={maxUses}
              onChange={(e) => setMaxUses(Math.max(1, parseInt(e.target.value, 10) || 1))}
              inputProps={{ min: 1, max: 10 }}
              helperText="1 = code à usage unique (recommandé)"
            />
            <TextField
              label="Expire dans (jours, optionnel)"
              type="number"
              size="small"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value ? parseInt(e.target.value, 10) : '')}
              inputProps={{ min: 1 }}
              helperText="Laisser vide = pas d'expiration"
            />

            <Button
              variant="contained"
              startIcon={creatingInv ? <CircularProgress size={18} color="inherit" /> : <AddCircleIcon />}
              onClick={handleCreateInvitation}
              disabled={creatingInv}
              fullWidth
            >
              Générer le code
            </Button>
          </Stack>

          {/* Résultat invitation créée */}
          {createdInvitation && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600} color="success.dark" sx={{ mb: 1 }}>
                ✅ Code créé !
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography
                  variant="h6"
                  fontFamily="monospace"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ flex: 1, bgcolor: 'white', px: 1.5, py: 0.5, borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}
                >
                  {createdInvitation.code}
                </Typography>
                <Tooltip title="Copier le lien">
                  <IconButton onClick={handleCopyLink} color="primary">
                    {copiedLink ? <CheckCircleIcon color="success" /> : <LinkIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={<WhatsAppIcon />}
                onClick={handleWhatsApp}
                sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
              >
                Envoyer par WhatsApp
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                {createdInvitation.link}
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* ── Promouvoir un utilisateur existant ── */}
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <PersonAddIcon color="secondary" />
            <Typography variant="h6" fontWeight={600}>Promouvoir un utilisateur</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Si la MID a déjà un compte EKAA, recherchez-la ici pour lui attribuer directement le rôle MID Supervisor.
          </Typography>

          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Email, nom ou téléphone..."
                value={promoteSearch}
                onChange={(e) => setPromoteSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                }}
              />
              <Button
                variant="outlined"
                onClick={handleSearch}
                disabled={searchLoading || !promoteSearch.trim()}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {searchLoading ? <CircularProgress size={18} /> : 'Chercher'}
              </Button>
            </Box>

            {promoteResults.length > 0 && (
              <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0 }}>
                {promoteResults.map((user, idx) => (
                  <React.Fragment key={user.id}>
                    {idx > 0 && <Divider />}
                    <ListItem
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => { setSelectedUser(user); setConfirmOpen(true); }}
                      secondaryAction={
                        <Button size="small" variant="contained" color="secondary" sx={{ fontSize: '0.7rem' }}>
                          Promouvoir
                        </Button>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Avatar src={user.avatarUrl || undefined} sx={{ width: 32, height: 32, fontSize: '0.85rem' }}>
                          {(user.displayName || user.firstName || '?').charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim()}
                        secondary={user.email || '—'}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}

            {promoteResults.length === 0 && promoteSearch && !searchLoading && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Aucun utilisateur trouvé. Essayez avec un email ou un nom complet.
              </Alert>
            )}
          </Stack>
        </Paper>
      </Grid>

      {/* Dialog confirmation promotion */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmer la promotion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Voulez-vous promouvoir <strong>{selectedUser?.displayName || selectedUser?.email}</strong> au rôle <strong>MID Supervisor</strong> ?
          </Typography>
          <Alert severity="info" sx={{ py: 0.5 }}>
            Cette action est réversible. Le rôle peut être retiré depuis la liste des MIDs.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handlePromote}
            disabled={promoting}
            startIcon={promoting ? <CircularProgress size={18} color="inherit" /> : <PersonAddIcon />}
          >
            Confirmer la promotion
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

// ─── Tab 4 : Candidatures à assigner ───────────────────────────────────────

interface AmbassadorApplication {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  university: string;
  status: string;
  submittedAt: string;
  referredByMid: boolean;
  midReferrerUserId: string | null;
}

interface AssignMidSelect {
  id: string;
  email: string;
  displayName: string;
}

function ApplicationsToAssignTab() {
  const notify = useNotify();
  const [applications, setApplications] = useState<AmbassadorApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState<string | null>(null);

  // Fetch MIDs using GraphQL
  const { data: midsData } = useQuery(GET_ADMIN_USERS, {
    variables: {
      filter: { role: 'MID_SUPERVISOR' },
      page: 1,
      pageSize: 100,
    },
    fetchPolicy: 'cache-and-network',
  });

  const mids: AssignMidSelect[] = (midsData?.adminUsers?.items || []).map((mid: any) => ({
    id: mid.id,
    email: mid.email || '',
    displayName: mid.displayName || mid.firstName || mid.email || 'MID',
  }));

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch spontaneous applications (without MID assigned)
      const appsResponse = await apiGet(
        '/api/ambassador-applications?hasNoMidAssigned=true&status=PENDING'
      );
      setApplications(appsResponse.data || []);
    } catch (e: any) {
      notify(`Erreur: ${e.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleAssignMid = async (applicationId: string, midUserId: string) => {
    setAssigning(applicationId);
    try {
      await fetch(`${API_URL}/api/ambassador-applications/${applicationId}/assign-mid`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify({ midUserId }),
      }).then(async (res) => {
        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.message || 'Erreur lors de l\'assignation');
        }
        return res.json();
      });

      notify('MID assigné avec succès', { type: 'success' });
      loadApplications();
    } catch (e: any) {
      notify(`Erreur: ${e.message}`, { type: 'error' });
    } finally {
      setAssigning(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (applications.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <PendingIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography color="text.secondary" variant="body1">
          Aucune candidature spontanée en attente
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Les candidatures sans MID referrer apparaîtront ici.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          {applications.length} candidature{applications.length > 1 ? 's' : ''} à traiter
        </Typography>
        <Tooltip title="Rafraîchir">
          <IconButton onClick={loadApplications} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell>Candidat</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Université</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Assigner MID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applications.map((app) => (
              <TableRow key={app.id} hover>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {app.firstName} {app.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {app.phone}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{app.email}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{app.university}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatDate(app.submittedAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={app.status === 'PENDING' ? 'En attente' : app.status}
                    size="small"
                    color={app.status === 'PENDING' ? 'warning' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <TextField
                      select
                      size="small"
                      sx={{ minWidth: 200 }}
                      placeholder="Sélectionner un MID"
                      disabled={assigning === app.id}
                      onChange={(e) => handleAssignMid(app.id, e.target.value)}
                      value=""
                      SelectProps={{
                        displayEmpty: true,
                      }}
                    >
                      <MenuItem value="" disabled>
                        Sélectionner un MID
                      </MenuItem>
                      {mids.map((mid) => (
                        <MenuItem key={mid.id} value={mid.id}>
                          {mid.displayName}
                        </MenuItem>
                      ))}
                    </TextField>
                    {assigning === app.id && <CircularProgress size={20} />}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Page principale ─────────────────────────────────────────────────────────

export function MidManagement() {
  const [tab, setTab] = useState(0);
  const [invRefresh, setInvRefresh] = useState(0);
  const [selectedMid, setSelectedMid] = useState<MidUser | null>(null);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);

  const { data: statsData } = useQuery(GET_ADMIN_USERS, {
    variables: { filter: { role: 'MID_SUPERVISOR' }, page: 1, pageSize: 1 },
    fetchPolicy: 'cache-and-network',
  });
  const { data: pendingAmbData } = useQuery(GET_ADMIN_USERS, {
    variables: { filter: { role: 'AMBASSADOR' }, page: 1, pageSize: 1 },
    fetchPolicy: 'cache-and-network',
  });

  const totalMids = statsData?.adminUsers?.total || 0;
  const totalAmbassadors = pendingAmbData?.adminUsers?.total || 0;

  const handleSuccess = () => {
    setInvRefresh((n) => n + 1);
  };

  const handleOpenTeamDialog = (mid: MidUser) => {
    setSelectedMid(mid);
    setTeamDialogOpen(true);
  };

  const handleCloseTeamDialog = () => {
    setTeamDialogOpen(false);
    setSelectedMid(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Gestion des MID" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Gestion Terrain — MID Supervisors</Typography>
          <Typography variant="body2" color="text.secondary">
            Onboardez les MID, gérez les invitations et suivez l'activité terrain
          </Typography>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatsCard
            title="MID Supervisors actives"
            value={totalMids}
            icon={<SupervisorAccountIcon />}
            color="#7C3AED"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatsCard
            title="Ambassadeurs terrain"
            value={totalAmbassadors}
            icon={<GroupsIcon />}
            color="#059669"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatsCard
            title="Invitations MID actives"
            value="—"
            icon={<CardGiftcardIcon />}
            color="#D97706"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<SupervisorAccountIcon fontSize="small" />}
            iconPosition="start"
            label="MIDs actives"
          />
          <Tab
            icon={<CardGiftcardIcon fontSize="small" />}
            iconPosition="start"
            label="Invitations"
          />
          <Tab
            icon={<PersonAddIcon fontSize="small" />}
            iconPosition="start"
            label="Onboarder"
          />
          <Tab
            icon={<PendingIcon fontSize="small" />}
            iconPosition="start"
            label="Candidatures à assigner"
          />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {tab === 0 && <MidListTab onPromote={handleOpenTeamDialog} />}
          {tab === 1 && <InvitationsTab refresh={invRefresh} />}
          {tab === 2 && <OnboardTab onSuccess={handleSuccess} />}
          {tab === 3 && <ApplicationsToAssignTab />}
        </Box>
      </Paper>

      {/* Dialog de gestion d'équipe */}
      <TeamManagementDialog
        mid={selectedMid}
        open={teamDialogOpen}
        onClose={handleCloseTeamDialog}
      />
    </Box>
  );
}

export default MidManagement;
